import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Armchair, RefreshCw, Users, CheckCircle2, Printer } from "lucide-react";
import { toast } from "sonner";

const Roster = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  const fetchRoster = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/roster/vacant-chairs");
      setData(res.data);
    } catch (e) {
      toast.error(`Failed to load roster: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setPrinting(true);
    try {
      const res = await apiClient.get("/roster/vacant-chairs.pdf", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "vacant_chairs.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Failed to download PDF: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPrinting(false);
    }
  };

  useEffect(() => {
    fetchRoster();
  }, []);

  const sections = data?.sections || [];
  const totalVacant = data?.total_vacant ?? 0;

  return (
    <div className="space-y-6" data-testid="roster-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl flex items-center gap-2">
            <Armchair className="w-6 h-6 text-primary" />
            Roster — Vacant Chairs
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Band-wide list of parts with no active member assigned. Deps and inactive
            members don't fill a chair — use this to spot who you still need to recruit.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadPdf}
            disabled={printing || loading || !data}
            data-testid="roster-print-btn"
          >
            <Printer className={`w-4 h-4 mr-2 ${printing ? "animate-pulse" : ""}`} />
            {printing ? "Building…" : "Print PDF"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRoster}
            disabled={loading}
            data-testid="roster-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="glass-card" data-testid="stat-sections-with-gaps">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Sections with vacant chairs</p>
            <p className="text-3xl font-heading mt-1 text-amber-400">{sections.length}</p>
          </CardContent>
        </Card>
        <Card className="glass-card" data-testid="stat-total-vacant">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Vacant chairs</p>
            <p className="text-3xl font-heading mt-1 text-red-400">{totalVacant}</p>
          </CardContent>
        </Card>
      </div>

      {loading && !data ? (
        <Card className="glass-card">
          <CardContent className="p-10 text-center text-muted-foreground">Loading…</CardContent>
        </Card>
      ) : sections.length === 0 ? (
        <Card className="glass-card">
          <CardContent
            className="p-10 text-center text-muted-foreground"
            data-testid="roster-empty-state"
          >
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-400/80" />
            <p className="text-base">Every chair is filled — nice one!</p>
            <p className="text-xs mt-1">
              Sections without defined parts don't appear here. Configure parts on the Sections
              page to track them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sections.map((s) => (
            <Card
              key={s.section_id}
              className="glass-card border-l-4 border-l-amber-500/70"
              data-testid={`roster-section-${s.section_id}`}
            >
              <CardHeader className="border-b border-white/5 pb-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    {s.section_name}
                  </CardTitle>
                  <Badge
                    variant="outline"
                    className="border-amber-500/40 text-amber-300 bg-amber-500/10"
                    data-testid={`roster-section-count-${s.section_id}`}
                  >
                    {s.vacant_count} of {s.total_parts} vacant
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <ul className="flex flex-wrap gap-2" data-testid={`roster-parts-${s.section_id}`}>
                  {s.vacant_parts.map((p) => (
                    <li key={p.id || p.name}>
                      <Badge
                        variant="outline"
                        className="border-red-500/40 text-red-300 bg-red-500/5 text-sm px-3 py-1"
                        data-testid={`roster-vacant-part-${s.section_id}-${(p.name || "").replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}
                      >
                        <Armchair className="w-3.5 h-3.5 mr-1.5" />
                        {p.name}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Roster;
